import { Skeleton } from '@savvy-kit/components/skeleton';
import { Text } from '@savvy-kit/foundations/text';

import type { AccountsSummary, AccountsSummaryLine } from './accounts-summary';
import { PortalCard, PortalCardFooter } from '../common/portal-section';
import { safeLargeMoney, safeMoney } from '../common/utils';
import {
  ACCOUNTS_SUMMARY_ASSETS_LABEL,
  ACCOUNTS_SUMMARY_ASSETS_TEST_ID,
  ACCOUNTS_SUMMARY_LIABILITIES_LABEL,
  ACCOUNTS_SUMMARY_LIABILITIES_TEST_ID,
  ACCOUNTS_SUMMARY_LOADING_TEST_ID,
  ACCOUNTS_SUMMARY_TITLE,
  NET_WORTH_LABEL,
} from './constants';

const SUMMARY_CARD_CLASS = 'flex h-full flex-col p-0';

function AssetsBar({ lines }: { readonly lines: readonly AccountsSummaryLine[] }) {
  return (
    <div
      aria-hidden="true"
      data-slot="accounts-summary-bar"
      className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
    >
      {lines
        .filter((line) => line.share > 0)
        .map((line) => (
          <div
            key={line.id}
            className="h-full min-w-0"
            style={{ flexGrow: line.share, flexBasis: 0, backgroundColor: line.color }}
          />
        ))}
    </div>
  );
}

function SummaryLines({ lines, testId }: { readonly lines: readonly AccountsSummaryLine[]; readonly testId: string }) {
  return (
    <ul className="flex flex-col gap-2" data-testid={testId}>
      {lines.map((line) => (
        <li key={line.id} className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            <span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: line.color }} />
            <Text variant="body-sm" color="dark" className="truncate">
              {line.label}
            </Text>
          </span>
          <Text variant="body-sm" color="dark" className="shrink-0 font-medium tabular-nums">
            {safeLargeMoney(line.magnitude)}
          </Text>
        </li>
      ))}
    </ul>
  );
}

function HalfTotal({
  label,
  total,
  destructive,
}: {
  readonly label: string;
  readonly total: number;
  readonly destructive?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Text variant="body-sm" color="dark" className="font-semibold">
        {label}
      </Text>
      <Text
        variant="body-sm"
        color={destructive === true ? 'destructive' : 'dark'}
        className="font-semibold tabular-nums"
        aria-label={`${label} total`}
        render={<span />}
      >
        {safeMoney(total)}
      </Text>
    </div>
  );
}

type AccountsSummaryCardProps = {
  readonly summary: AccountsSummary;
  readonly netWorth: number;
};

export function AccountsSummaryCard({ summary, netWorth }: AccountsSummaryCardProps) {
  const showAssets = summary.assetLines.length > 0;
  const showLiabilities = summary.liabilityLines.length > 0;

  return (
    <PortalCard title={ACCOUNTS_SUMMARY_TITLE} padded={false} className={SUMMARY_CARD_CLASS}>
      <div className="flex flex-col gap-4 px-6 pb-4">
        {showAssets && (
          <div className="flex flex-col gap-3">
            <HalfTotal label={ACCOUNTS_SUMMARY_ASSETS_LABEL} total={summary.assetsTotal} />
            <AssetsBar lines={summary.assetLines} />
            <SummaryLines lines={summary.assetLines} testId={ACCOUNTS_SUMMARY_ASSETS_TEST_ID} />
          </div>
        )}

        {showAssets && showLiabilities && <div className="border-t border-border-light" />}

        {showLiabilities && (
          <div className="flex flex-col gap-3">
            <HalfTotal label={ACCOUNTS_SUMMARY_LIABILITIES_LABEL} total={-summary.liabilitiesTotal} destructive />
            <SummaryLines lines={summary.liabilityLines} testId={ACCOUNTS_SUMMARY_LIABILITIES_TEST_ID} />
          </div>
        )}
      </div>

      <PortalCardFooter className="mt-auto flex items-center justify-between gap-2 bg-background-light">
        <Text variant="body-sm" color="dark" className="font-semibold">
          {NET_WORTH_LABEL}
        </Text>
        <Text
          variant="body-sm"
          color="dark"
          className="font-semibold tabular-nums"
          aria-label={`${ACCOUNTS_SUMMARY_TITLE} ${NET_WORTH_LABEL}`}
          render={<span />}
        >
          {safeMoney(netWorth)}
        </Text>
      </PortalCardFooter>
    </PortalCard>
  );
}

export function AccountsSummaryCardSkeleton() {
  return (
    <PortalCard title={ACCOUNTS_SUMMARY_TITLE} padded={false} className={SUMMARY_CARD_CLASS}>
      <div className="flex flex-col gap-4 px-6 pb-6" data-testid={ACCOUNTS_SUMMARY_LOADING_TEST_ID}>
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-2 w-full rounded-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
      </div>
    </PortalCard>
  );
}
